'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import ResourceCard from './ResourceCard';
import CategoryFilter from './CategoryFilter';
import SearchBar from './SearchBar';
import TypeFilter from './TypeFilter';
import FeaturedToolkit from './FeaturedToolkit';
import ForgivenessAssessment from './ForgivenessAssessment';
import VideoTutorial from './VideoTutorial';
import { adminContentService, ManagedResource, ManagedToolkit, ManagedVideo } from '@/lib/services/adminContentService';

interface Question {
  id: string;
  question: string;
  options: string[];
}

const ResourcesHubInteractive = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('All Resources');
  const [selectedType, setSelectedType] = useState('All Types');
  const [searchQuery, setSearchQuery] = useState('');
  const [bookmarkedResources, setBookmarkedResources] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('browse');
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<{[key: string]: string;}>({});
  const [showResults, setShowResults] = useState(false);
  const [assessmentScore, setAssessmentScore] = useState(0);

  // Supabase data
  const [resources, setResources] = useState<ManagedResource[]>([]);
  const [toolkits, setToolkits] = useState<ManagedToolkit[]>([]);
  const [videos, setVideos] = useState<ManagedVideo[]>([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    setIsHydrated(true);
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setDataLoading(true);
      const [res, tk, vd] = await Promise.all([
        adminContentService.getResources(),
        adminContentService.getToolkits(),
        adminContentService.getVideos(),
      ]);
      setResources(res.filter(r => r.is_visible));
      setToolkits(tk.filter(t => t.is_visible));
      setVideos(vd.filter(v => v.is_visible));
    } catch (e) {
      console.warn('Failed to load resources from database');
    } finally {
      setDataLoading(false);
    }
  };

  const categories = [
    'All Resources',
    'Peace Education',
    'Forgiveness Framework',
    'Community Organizing',
    'Research Library',
    'Media Resources',
    'Personal Transformation',
  ];

  const types = [
    { label: 'All Types', icon: 'Squares2X2Icon' },
    { label: 'PDF', icon: 'DocumentTextIcon' },
    { label: 'Video', icon: 'VideoCameraIcon' },
    { label: 'Audio', icon: 'SpeakerWaveIcon' },
    { label: 'Toolkit', icon: 'WrenchScrewdriverIcon' },
  ];

  // Map ManagedResource to the shape ResourceCard expects
  const mappedResources = resources.map(r => ({
    id: r.id,
    title: r.title,
    description: r.description,
    category: r.category,
    type: r.type,
    image: r.image_url,
    alt: r.alt_text,
    downloads: r.downloads,
    rating: r.rating,
    languages: r.languages,
    fileSize: r.file_size || undefined,
    duration: r.duration || undefined,
    pages: r.pages || undefined,
  }));

  // Map ManagedToolkit to the shape FeaturedToolkit expects
  const mappedToolkits = toolkits.map(t => ({
    id: t.id,
    title: t.title,
    description: t.description,
    image: t.image_url,
    alt: t.alt_text,
    userType: t.user_type,
    includes: t.includes,
  }));

  // Map ManagedVideo to the shape VideoTutorial expects
  const mappedVideos = videos.map(v => ({
    id: v.id,
    title: v.title,
    description: v.description,
    thumbnail: v.thumbnail_url,
    alt: v.alt_text,
    duration: v.duration,
    views: v.views,
    category: v.category,
  }));

  const assessmentQuestions: Question[] = [
    {
      id: 'q1',
      question: 'When someone hurts me, my first reaction is usually to:',
      options: ['Seek revenge or retaliation', 'Withdraw and avoid the person', 'Try to understand their perspective', 'Immediately forgive and move forward'],
    },
    {
      id: 'q2',
      question: 'I believe forgiveness is:',
      options: ['A sign of weakness', 'Only possible in certain situations', 'A personal choice that takes time', 'Essential for my own peace and healing'],
    },
    {
      id: 'q3',
      question: 'When I think about past hurts, I:',
      options: ['Feel intense anger and resentment', 'Try to distract myself and not think about it', 'Acknowledge the pain but work through it', 'Feel at peace and have released the hurt'],
    },
    {
      id: 'q4',
      question: 'My ability to forgive myself for my own mistakes is:',
      options: ['Very difficult; I hold onto guilt', "Challenging but I'm working on it", 'Moderate; depends on the situation', 'Strong; I practice self-compassion'],
    },
    {
      id: 'q5',
      question: 'In conflicts, I am most likely to:',
      options: ['Escalate the situation', 'Avoid confrontation entirely', 'Seek mediation or dialogue', 'Lead reconciliation efforts'],
    },
  ];

  const handleBookmark = (id: string) => {
    if (!isHydrated) return;
    setBookmarkedResources(prev => prev.includes(id) ? prev.filter(resId => resId !== id) : [...prev, id]);
  };

  const handleAnswerSelect = (questionId: string, answer: string) => {
    setSelectedAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleNext = () => { if (currentQuestion < assessmentQuestions.length - 1) setCurrentQuestion(prev => prev + 1); };
  const handlePrevious = () => { if (currentQuestion > 0) setCurrentQuestion(prev => prev - 1); };

  const handleSubmit = () => {
    const scoreMap: {[key: string]: number;} = {
      'Seek revenge or retaliation': 0, 'Withdraw and avoid the person': 25, 'Try to understand their perspective': 75, 'Immediately forgive and move forward': 100,
      'A sign of weakness': 0, 'Only possible in certain situations': 50, 'A personal choice that takes time': 75, 'Essential for my own peace and healing': 100,
      'Feel intense anger and resentment': 0, 'Try to distract myself and not think about it': 25, 'Acknowledge the pain but work through it': 75, 'Feel at peace and have released the hurt': 100,
      'Very difficult; I hold onto guilt': 0, "Challenging but I'm working on it": 50, 'Moderate; depends on the situation': 75, 'Strong; I practice self-compassion': 100,
      'Escalate the situation': 0, 'Avoid confrontation entirely': 25, 'Seek mediation or dialogue': 75, 'Lead reconciliation efforts': 100,
    };
    let totalScore = 0;
    Object.values(selectedAnswers).forEach(answer => { totalScore += scoreMap[answer] || 0; });
    setAssessmentScore(Math.round(totalScore / assessmentQuestions.length));
    setShowResults(true);
  };

  const handleVideoClick = (videoId: string) => { console.log('Playing video:', videoId); };

  const filteredResources = mappedResources.filter(resource => {
    const matchesCategory = selectedCategory === 'All Resources' || resource.category === selectedCategory;
    const matchesType = selectedType === 'All Types' || resource.type === selectedType;
    const matchesSearch = searchQuery === '' || resource.title.toLowerCase().includes(searchQuery.toLowerCase()) || resource.description.toLowerCase().includes(searchQuery.toLowerCase()) || resource.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesType && matchesSearch;
  });

  if (!isHydrated || dataLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="h-20" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded w-1/3" />
            <div className="h-64 bg-muted rounded" />
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="h-96 bg-muted rounded" />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="h-20" />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-healing-blue to-peaceful-teal text-white py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-headline font-bold mb-6 animate-fade-in">Resources Hub</h1>
            <p className="text-xl font-body mb-8 opacity-90">
              Access comprehensive peace education materials, forgiveness frameworks, community organizing toolkits, and evidence-based research to support your transformation journey.
            </p>
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-lg">
                <Icon name="DocumentTextIcon" size={24} />
                <span className="font-body font-medium">{resources.length}+ Resources</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-lg">
                <Icon name="GlobeAltIcon" size={24} />
                <span className="font-body font-medium">15 Languages</span>
              </div>
              <div className="flex items-center space-x-2 bg-white/20 px-4 py-2 rounded-lg">
                <Icon name="UserGroupIcon" size={24} />
                <span className="font-body font-medium">500K+ Downloads</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation Tabs */}
      <section className="bg-card border-b border-border sticky top-20 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex space-x-1 overflow-x-auto">
            {[
              { id: 'browse', label: 'Browse Resources', icon: 'Squares2X2Icon' },
              { id: 'toolkits', label: 'Featured Toolkits', icon: 'WrenchScrewdriverIcon' },
              { id: 'assessment', label: 'Forgiveness Assessment', icon: 'ClipboardDocumentCheckIcon' },
              { id: 'videos', label: 'Video Tutorials', icon: 'VideoCameraIcon' },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 px-6 py-4 font-body text-sm font-medium whitespace-nowrap transition-all duration-300 border-b-2 ${activeTab === tab.id ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'}`}>
                <Icon name={tab.icon as any} size={20} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Browse Resources Tab */}
      {activeTab === 'browse' && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="mb-12 space-y-6">
              <SearchBar searchQuery={searchQuery} onSearchChange={setSearchQuery} />
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                <div className="flex-1">
                  <h3 className="text-sm font-body font-semibold text-foreground mb-3">Filter by Category</h3>
                  <CategoryFilter categories={categories} selectedCategory={selectedCategory} onCategoryChange={setSelectedCategory} />
                </div>
                <div className="flex-1">
                  <h3 className="text-sm font-body font-semibold text-foreground mb-3">Filter by Type</h3>
                  <TypeFilter types={types} selectedType={selectedType} onTypeChange={setSelectedType} />
                </div>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-sm font-body text-muted-foreground">Showing {filteredResources.length} of {mappedResources.length} resources</p>
            </div>
            {filteredResources.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredResources.map(resource => (
                  <ResourceCard key={resource.id} resource={resource} onBookmark={handleBookmark} isBookmarked={bookmarkedResources.includes(resource.id)} />
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <Icon name="MagnifyingGlassIcon" size={64} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-xl font-headline font-bold text-foreground mb-2">No resources found</h3>
                <p className="text-base font-body text-muted-foreground">Try adjusting your filters or search query</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Featured Toolkits Tab */}
      {activeTab === 'toolkits' && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="mb-12">
              <h2 className="text-3xl font-headline font-bold text-foreground mb-4">Featured Toolkits</h2>
              <p className="text-lg font-body text-muted-foreground max-w-3xl">Comprehensive resource packages designed for different user types. Each toolkit includes everything you need to begin your peace-building journey.</p>
            </div>
            <FeaturedToolkit toolkits={mappedToolkits} />
          </div>
        </section>
      )}

      {/* Forgiveness Assessment Tab */}
      {activeTab === 'assessment' && (
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="mb-12 text-center">
              <h2 className="text-3xl font-headline font-bold text-foreground mb-4">Forgiveness Assessment</h2>
              <p className="text-lg font-body text-muted-foreground max-w-2xl mx-auto">Discover your current forgiveness capacity and receive personalized resource recommendations to support your healing journey.</p>
            </div>
            <ForgivenessAssessment questions={assessmentQuestions} currentQuestion={currentQuestion} selectedAnswers={selectedAnswers} onAnswerSelect={handleAnswerSelect} onNext={handleNext} onPrevious={handlePrevious} onSubmit={handleSubmit} showResults={showResults} assessmentScore={assessmentScore} />
          </div>
        </section>
      )}

      {/* Video Tutorials Tab */}
      {activeTab === 'videos' && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="mb-12">
              <h2 className="text-3xl font-headline font-bold text-foreground mb-4">Video Tutorial Library</h2>
              <p className="text-lg font-body text-muted-foreground max-w-3xl">Learn from expert facilitators, community organizers, and peace-building practitioners through our comprehensive video tutorial collection.</p>
            </div>
            <VideoTutorial videos={mappedVideos} onVideoClick={handleVideoClick} />
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="bg-gradient-to-br from-primary to-secondary text-white py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-headline font-bold mb-6">Can't Find What You're Looking For?</h2>
          <p className="text-xl font-body mb-8 opacity-90">We're constantly adding new resources. Submit a request for specific materials or contribute your own resources to help others on their peace-building journey.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-3 bg-white text-primary rounded-md font-cta text-base font-bold transition-all duration-300 hover:scale-105 hover:shadow-lg">Request a Resource</button>
            <button className="px-8 py-3 bg-accent text-accent-foreground rounded-md font-cta text-base font-bold transition-all duration-300 hover:scale-105 hover:shadow-lg">Contribute Resources</button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResourcesHubInteractive;